/**
 * Mover una cita eligiendo día, profesional y un hueco libre.
 *
 * El date/time a pelo no dice qué está ocupado. Aquí se pide ESE día a la
 * agenda y se enseña, por profesional, qué reservas hay y dónde cabe la que
 * se está moviendo. Pinchar un hueco elige la hora; «Mover aquí» escribe.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../auth'
import { api, ErrorApi } from '../lib/api'
import { patchMoverCita } from './calendario-acciones'
import {
    aHhmm,
    aMinutos,
    huecosLibres,
    iniciosQueCaben,
    jornadaDe,
    type PlanSemanal,
} from './calendario-geometria'

interface CitaDia {
    id: number
    dia: string
    inicio: string
    minutos: number
    profesionalId: number | null
    cliente: { nombre: string; apellidos: string; telefono: string } | null
}

interface DiaAgenda {
    agenda: { alcanzable: boolean; motivo: string | null }
    citas: CitaDia[]
    profesionales: { id: number; nombre: string; plan: PlanSemanal; color: string }[]
    bloqueos: { lista: { dia: string; inicio: string; profesionalId: number }[] }
}

export function BotonMoverCita({
    accountId,
    citaId,
    dia,
    horaInicio,
    minutos,
    profesionalId,
    alHecho,
    forzarAbierto = false,
}: {
    accountId: string
    citaId: number
    dia: string
    horaInicio: string
    minutos: number
    profesionalId: number | null
    alHecho?: (() => void) | undefined
    /** Abierto de entrada: el menú del mes, no el botón «Mover». */
    forzarAbierto?: boolean | undefined
}) {
    const soloLectura = useSoloLectura()
    const qc = useQueryClient()
    const [abierto, setAbierto] = useState(forzarAbierto)
    const [nuevoDia, setNuevoDia] = useState(dia)
    const [hora, setHora] = useState(horaInicio.length === 5 ? horaInicio : horaInicio.slice(0, 5))
    const [destinoProf, setDestinoProf] = useState<number | null>(profesionalId)
    const [error, setError] = useState<string | null>(null)

    const diaQ = useQuery({
        queryKey: ['calendario', accountId, 'mover', nuevoDia],
        queryFn: () =>
            api.get<DiaAgenda>(
                `/business/${accountId}/calendar?desde=${nuevoDia}&hasta=${nuevoDia}&incluirTests=1`,
            ),
        enabled: abierto,
    })

    const mut = useMutation({
        mutationFn: () => {
            const id =
                destinoProf ?? diaQ.data?.profesionales[0]?.id ?? profesionalId
            return patchMoverCita(accountId, citaId, nuevoDia, hora, minutos, id)
        },
        onSuccess: async () => {
            setAbierto(false)
            setError(null)
            await qc.invalidateQueries({ queryKey: ['calendario', accountId] })
            alHecho?.()
        },
        onError: (e) => {
            if (e instanceof ErrorApi && e.message === 'fuera_horario') {
                setError('Fuera del horario del profesional')
                return
            }
            if (e instanceof ErrorApi && e.message === 'solape') {
                setError('Esa franja ya está ocupada')
                return
            }
            setError(e instanceof ErrorApi ? e.message : 'No se pudo mover')
        },
    })

    if (soloLectura) return null
    if (!abierto) {
        return (
            <button
                type="button"
                data-testid={`mover-cita-${citaId}`}
                onClick={() => setAbierto(true)}
                className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-200 hover:bg-neutral-800"
            >
                Mover
            </button>
        )
    }

    const data = diaQ.data
    const profes = data?.profesionales ?? []
    const elegido = profes.find((p) => p.id === destinoProf) ?? profes[0]
    const profId = elegido?.id ?? destinoProf

    return (
        <div
            className="flex w-full flex-col gap-3 rounded-md border border-neutral-800 bg-neutral-950/60 p-3"
            data-testid="mover-cita-dia"
        >
            <div className="flex flex-wrap gap-2">
                <input
                    type="date"
                    value={nuevoDia}
                    onChange={(e) => setNuevoDia(e.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-sm text-neutral-200"
                />
                <input
                    type="time"
                    step={1800}
                    value={hora}
                    onChange={(e) => setHora(e.target.value)}
                    className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-sm text-neutral-200"
                />
            </div>

            {profes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {profes.map((p) => {
                        const activo = p.id === profId
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setDestinoProf(p.id)}
                                aria-pressed={activo}
                                className={
                                    'rounded-full border px-2.5 py-1 text-xs ' +
                                    (activo
                                        ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
                                        : 'border-neutral-800 text-neutral-400 hover:text-neutral-200')
                                }
                            >
                                {p.nombre || `Profesional ${p.id}`}
                            </button>
                        )
                    })}
                </div>
            )}

            <ReservasDelDia
                cargando={diaQ.isPending}
                alcanzable={data?.agenda.alcanzable ?? true}
                motivo={data?.agenda.motivo ?? null}
                citas={data?.citas ?? []}
                bloqueos={data?.bloqueos.lista ?? []}
                citaId={citaId}
                dia={nuevoDia}
                profId={profId ?? null}
                plan={elegido?.plan}
                duracion={minutos}
                horaElegida={hora}
                alElegirHora={setHora}
            />

            {error && <p className="text-xs text-rose-400">{error}</p>}
            <div className="flex gap-2">
                <button
                    type="button"
                    disabled={mut.isPending}
                    onClick={() => mut.mutate()}
                    className="rounded-md bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-500 disabled:opacity-50"
                >
                    {mut.isPending ? 'Moviendo…' : 'Mover aquí'}
                </button>
                {!forzarAbierto && (
                    <button
                        type="button"
                        onClick={() => setAbierto(false)}
                        className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800"
                    >
                        Atrás
                    </button>
                )}
            </div>
        </div>
    )
}

function ReservasDelDia({
    cargando,
    alcanzable,
    motivo,
    citas,
    bloqueos,
    citaId,
    dia,
    profId,
    plan,
    duracion,
    horaElegida,
    alElegirHora,
}: {
    cargando: boolean
    alcanzable: boolean
    motivo: string | null
    citas: CitaDia[]
    bloqueos: { dia: string; inicio: string; profesionalId: number }[]
    citaId: number
    dia: string
    profId: number | null
    plan: PlanSemanal | undefined
    duracion: number
    horaElegida: string
    alElegirHora: (hhmm: string) => void
}) {
    if (cargando) {
        return <p className="text-xs text-neutral-500">Cargando las reservas de este día…</p>
    }
    if (!alcanzable) {
        return (
            <p className="text-xs text-amber-400">
                No se ha podido leer el día{motivo ? ` (${motivo})` : ''}. Se puede escribir la hora a mano.
            </p>
        )
    }

    const delProf = citas.filter((c) => c.dia === dia && c.profesionalId === profId)
    const ocupadas = delProf
        .filter((c) => c.id !== citaId)
        .map((c) => {
            const ini = aMinutos(c.inicio) ?? 0
            return { desdeMin: ini, hastaMin: ini + Math.max(1, c.minutos), cita: c }
        })
        .sort((a, b) => a.desdeMin - b.desdeMin)

    for (const b of bloqueos) {
        if (b.dia !== dia || b.profesionalId !== profId) continue
        const ini = aMinutos(b.inicio)
        if (ini === null) continue
        ocupadas.push({
            desdeMin: ini,
            hastaMin: ini + 15,
            cita: {
                id: -ini,
                dia,
                inicio: b.inicio,
                minutos: 15,
                profesionalId: b.profesionalId,
                cliente: { nombre: 'Retenido', apellidos: '', telefono: '' },
            },
        })
    }
    ocupadas.sort((a, b) => a.desdeMin - b.desdeMin)

    const jornada = jornadaDe(plan, dia)
    const libres = huecosLibres(
        jornada,
        ocupadas.map((o) => ({ desdeMin: o.desdeMin, hastaMin: o.hastaMin })),
    )

    type Fila =
        | { clave: string; tipo: 'ocupada'; desdeMin: number; hastaMin: number; texto: string }
        | { clave: string; tipo: 'libre'; desdeMin: number; hastaMin: number; inicios: number[] }

    const filas: Fila[] = [
        ...ocupadas.map((o) => ({
            clave: `o-${o.cita.id}-${o.desdeMin}`,
            tipo: 'ocupada' as const,
            desdeMin: o.desdeMin,
            hastaMin: o.hastaMin,
            texto: nombreDe(o.cita),
        })),
        ...libres.map((h) => ({
            clave: `l-${h.desdeMin}`,
            tipo: 'libre' as const,
            desdeMin: h.desdeMin,
            hastaMin: h.hastaMin,
            inicios: iniciosQueCaben(h, duracion),
        })),
    ].sort((a, b) => a.desdeMin - b.desdeMin || (a.tipo === 'ocupada' ? -1 : 1))

    return (
        <div className="max-h-64 overflow-y-auto rounded-md border border-neutral-800">
            {jornada.length === 0 && (
                <p className="border-b border-neutral-800 px-2 py-1.5 text-xs text-amber-400">
                    Este profesional no trabaja este día.
                </p>
            )}
            {filas.length === 0 && jornada.length > 0 && (
                <p className="px-2 py-2 text-xs text-neutral-500">Sin reservas. El día está libre.</p>
            )}
            {filas.map((f) =>
                f.tipo === 'ocupada' ? (
                    <div
                        key={f.clave}
                        className="flex items-baseline justify-between gap-2 border-b border-neutral-900 px-2 py-1.5 text-xs last:border-b-0"
                    >
                        <span className="shrink-0 tabular-nums text-neutral-400">
                            {aHhmm(f.desdeMin)}–{aHhmm(f.hastaMin)}
                        </span>
                        <span className="truncate text-neutral-200">{f.texto}</span>
                    </div>
                ) : (
                    <HuecoLibre
                        key={f.clave}
                        desdeMin={f.desdeMin}
                        hastaMin={f.hastaMin}
                        inicios={f.inicios}
                        horaElegida={horaElegida}
                        alElegirHora={alElegirHora}
                    />
                ),
            )}
        </div>
    )
}

function HuecoLibre({
    desdeMin,
    hastaMin,
    inicios,
    horaElegida,
    alElegirHora,
}: {
    desdeMin: number
    hastaMin: number
    inicios: number[]
    horaElegida: string
    alElegirHora: (hhmm: string) => void
}) {
    return (
        <div className="border-b border-neutral-900 px-2 py-1.5 last:border-b-0">
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
                <span className="tabular-nums text-emerald-400/90">
                    {aHhmm(desdeMin)}–{aHhmm(hastaMin)}
                </span>
                <span className="text-emerald-500/80">libre</span>
            </div>
            {inicios.length === 0 ? (
                <p className="text-[11px] text-neutral-600">No cabe esta cita aquí.</p>
            ) : (
                <div className="flex flex-wrap gap-1">
                    {inicios.map((m) => {
                        const hhmm = aHhmm(m)
                        const activo = hhmm === horaElegida
                        return (
                            <button
                                key={m}
                                type="button"
                                onClick={() => alElegirHora(hhmm)}
                                className={
                                    'rounded px-1.5 py-0.5 text-[11px] tabular-nums ' +
                                    (activo
                                        ? 'bg-sky-500/25 text-sky-200'
                                        : 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800')
                                }
                            >
                                {hhmm}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function nombreDe(c: CitaDia): string {
    const x = c.cliente
    if (!x) return 'Cita'
    const apellidos = x.apellidos.trim() === '-' ? '' : x.apellidos
    return `${x.nombre} ${apellidos}`.trim() || 'Cita'
}
